import { dashboardApi } from '../api/modules/dashboard.api';
import { getApiErrorMessage } from '../api/client';
import { USE_API } from '../config/api';
import { peopleMasterSeed } from '../data/master/people';
import { vendorMasterSeed } from '../data/master/vendors';
import { indentSeed } from '../data/master/indents';
import { getAssets } from '../data/master/equipmentAssetsStore';
import { downloadExcelResponse, exportFileStamp } from '../utils/exportExcel';

function mockCounts() {
  const villagers = peopleMasterSeed.filter((p) => p.active !== false);
  const vendors = vendorMasterSeed.filter((v) => v.active !== false);
  const equipment = getAssets();
  const indents = indentSeed;

  const countStatus = (list, key, value) =>
    list.filter((row) => String(row[key] || '').toLowerCase() === value).length;

  return {
    villages: {
      total: 59,
      active: 59,
    },
    villagers: {
      total: villagers.length,
      pendingKyc: countStatus(villagers, 'verificationStatus', 'pending'),
      verified: countStatus(villagers, 'verificationStatus', 'verified'),
    },
    vendors: {
      total: vendors.length,
      pending: countStatus(vendors, 'verificationStatus', 'pending'),
      verified: countStatus(vendors, 'verificationStatus', 'verified'),
      blocked: countStatus(vendors, 'verificationStatus', 'blocked'),
      agencies: {
        total: Math.ceil(vendors.length / 2),
        pending: 0,
        verified: Math.ceil(vendors.length / 2),
        blocked: 0,
      },
      serviceProviders: {
        total: Math.floor(vendors.length / 2),
        pending: countStatus(vendors, 'verificationStatus', 'pending'),
        verified: countStatus(vendors, 'verificationStatus', 'verified'),
        blocked: countStatus(vendors, 'verificationStatus', 'blocked'),
      },
      specifications: {
        agency: Math.ceil(vendors.length / 2),
        equipment: Math.floor(vendors.length / 2),
        materialSupply: 1,
        specialServices: 1,
      },
    },
    materials: {
      total: 3,
      pending: 1,
      verified: 2,
      rejected: 0,
    },
    specialServices: {
      members: 1,
      tags: { Manpower: 1, Civil: 1 },
    },
    equipment: {
      total: equipment.length,
      available: countStatus(equipment, 'operationalStatus', 'available'),
      deployed: countStatus(equipment, 'operationalStatus', 'deployed'),
      pendingKyc: countStatus(equipment, 'status', 'pending'),
      rejected: countStatus(equipment, 'status', 'rejected'),
      verified: countStatus(equipment, 'status', 'verified'),
    },
    indents: {
      total: indents.length,
      pending: countStatus(indents, 'status', 'pending'),
      recommended: countStatus(indents, 'status', 'recommended'),
      assigned: countStatus(indents, 'status', 'assigned'),
      released: countStatus(indents, 'status', 'released'),
      cancelled: countStatus(indents, 'status', 'cancelled'),
      inProgress: countStatus(indents, 'status', 'in_progress'),
      open:
        countStatus(indents, 'status', 'pending') +
        countStatus(indents, 'status', 'recommended') +
        countStatus(indents, 'status', 'assigned') +
        countStatus(indents, 'status', 'in_progress'),
      lineTypes: {
        equipment: indents.length,
        material: 0,
        specialService: 0,
      },
    },
    staff: {
      total: 3,
      active: 3,
    },
    businessUnits: {
      total: 2,
      active: 2,
      sites: 4,
    },
  };
}

export async function getDashboardCounts() {
  if (!USE_API) return mockCounts();
  try {
    const data = await dashboardApi.getCounts();
    const indents = data?.indents || {};
    if (indents.open == null) {
      const pending = Number(indents.pending) || 0;
      const recommended = Number(indents.recommended) || 0;
      const assigned = Number(indents.assigned) || 0;
      const inProgress = Number(indents.inProgress) || 0;
      indents.open = pending + recommended + assigned + inProgress;
      indents.inProgress = inProgress;
    }
    return { ...data, indents };
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load dashboard'));
  }
}

export async function getVillageSummary() {
  if (!USE_API) {
    return [];
  }
  try {
    const rows = await dashboardApi.villageSummary({ includeEmpty: 'true' });
    return (Array.isArray(rows) ? rows : []).map((row) => ({
      villageId: row.villageId ?? null,
      villageName: row.villageName || '',
      memberCount: Number(row.memberCount) || 0,
      materialCount: Number(row.materialCount) || 0,
      equipmentCount: Number(row.equipmentCount) || 0,
      recommendedCount: Number(row.recommendedCount) || 0,
      deployedCount: Number(row.deployedCount) || 0,
      releasedCount: Number(row.releasedCount) || 0,
      avgRating: row.avgRating != null ? Number(row.avgRating) : null,
    }));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load village summary'));
  }
}

export async function exportVillageSummaryExcel({ includeEmpty = true } = {}) {
  if (!USE_API) {
    throw new Error('Excel export requires API mode');
  }
  try {
    const response = await dashboardApi.exportVillageSummary({
      includeEmpty: includeEmpty ? 'true' : 'false',
    });
    return downloadExcelResponse(
      response,
      `village_report_${exportFileStamp()}.xlsx`,
    );
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to export village report'));
  }
}

export async function getBuSiteSummary({ includeEmpty = false } = {}) {
  if (!USE_API) {
    return [];
  }
  try {
    const rows = await dashboardApi.buSiteSummary(
      includeEmpty ? { includeEmpty: 'true' } : {},
    );
    return (Array.isArray(rows) ? rows : []).map((row) => ({
      buId: row.buId ?? null,
      buCode: row.buCode || '',
      buName: row.buName || '',
      siteId: row.siteId != null ? Number(row.siteId) : null,
      siteCode: row.siteCode || '',
      siteName: row.siteName || '',
      projectOperation: row.projectOperation || '',
      requestTotal: Number(row.requestTotal) || 0,
      requestPending: Number(row.requestPending) || 0,
      requestRecommended: Number(row.requestRecommended) || 0,
      requestAssigned: Number(row.requestAssigned) || 0,
      requestReleased: Number(row.requestReleased) || 0,
      requestCancelled: Number(row.requestCancelled) || 0,
      deploymentCount: Number(row.deploymentCount) || 0,
      releaseCount: Number(row.releaseCount) || 0,
      activeDeployments: Number(row.activeDeployments) || 0,
      avgRating: row.avgRating != null ? Number(row.avgRating) : null,
      avgTurnaroundDays:
        row.avgTurnaroundDays != null ? Number(row.avgTurnaroundDays) : null,
      vendorTotal: Number(row.vendorTotal) || 0,
      vendorPending: Number(row.vendorPending) || 0,
      vendorVerified: Number(row.vendorVerified) || 0,
      equipmentAvailable: Number(row.equipmentAvailable) || 0,
    }));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load BU / Site summary'));
  }
}

export async function exportBuSiteSummaryExcel({ includeEmpty = false } = {}) {
  if (!USE_API) {
    throw new Error('Excel export requires API mode');
  }
  try {
    const response = await dashboardApi.exportBuSiteSummary(
      includeEmpty ? { includeEmpty: 'true' } : {},
    );
    return downloadExcelResponse(
      response,
      `bu_site_report_${exportFileStamp()}.xlsx`,
    );
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to export BU / Site report'));
  }
}
